import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getBusinessTransactionsPaged, getComponents, getTenants } from '../../shared/api/api';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import FilterDropdown from '../../shared/components/FilterDropdown';
import useResizableColumns, { ICON_COL_MIN_WIDTH } from '../../shared/hooks/useResizableColumns';
import useUpdateEffect from '../../shared/hooks/useUpdateEffect';
import '../../shared/styles/resizableTable.css';
import './BusinessTransactionsPage.css';

const PAGE_SIZE = 25;

const COLUMNS = [
  { key: 'id', label: 'ID', width: 110 },
  { key: 'transactionId', label: 'Transaction ID', width: 130 },
  { key: 'timestamp', label: 'Timestamp', width: 150 },
  { key: 'componentName', label: 'Component', width: 180 },
  { key: 'environment', label: 'Environment', width: 110 },
  { key: 'httpMethod', label: 'Method', width: 90 },
  { key: 'endpoint', label: 'Endpoint', width: 340 },
  { key: 'statusCode', label: 'Status', width: 80 },
  { key: 'durationMs', label: 'Duration', width: 90 },
  { key: 'userId', label: 'User', width: 110 },
  { key: 'tenantId', label: 'Tenant', width: 110 },
  { key: 'arrow', label: '', width: 36, minWidth: ICON_COL_MIN_WIDTH },
];

function BtDropdown({ value, placeholder, options, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const label = value || placeholder;
  const isPlaceholder = !value;

  return (
    <div className="bt-dropdown" ref={ref}>
      <button
        className={`bt-dropdown-btn${isPlaceholder ? ' bt-dropdown-btn--placeholder' : ''}`}
        onClick={() => setOpen((o) => !o)}
        type="button"
      >
        <span className="bt-dropdown-val">{label}</span>
        <svg
          className={`bt-dropdown-chevron${open ? ' bt-dropdown-chevron--open' : ''}`}
          viewBox="0 0 24 24" width="14" height="14" fill="none"
          stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div className="bt-dropdown-menu">
          <div
            className={`bt-dropdown-item bt-dropdown-item--placeholder${!value ? ' bt-dropdown-item--active' : ''}`}
            onClick={() => { onChange(''); setOpen(false); }}
          >
            {placeholder}
          </div>
          {options.map((opt) => (
            <div
              key={opt}
              className={`bt-dropdown-item${opt === value ? ' bt-dropdown-item--active' : ''}`}
              onClick={() => { onChange(opt); setOpen(false); }}
            >
              <div className="bt-dropdown-item-dot" />
              {opt}
              {opt === value && (
                <svg className="bt-dropdown-check" viewBox="0 0 24 24" width="14" height="14" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BusinessTransactionsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [components, setComponents] = useState([]);
  const [componentsLoading, setComponentsLoading] = useState(true);
  const [componentsError, setComponentsError] = useState('');

  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState(searchParams.get('tenant') || '');
  const [componentName, setComponentName] = useState(searchParams.get('component') || '');

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [pageToken, setPageToken] = useState(null);
  const [isLastPage, setIsLastPage] = useState(false);

  // Client-side pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Filters, search, sort and time window are restorable from the URL for the
  // same reason page is (see handleRowClick / the mount effect below): a
  // fresh visit has none of these params and gets the same defaults as
  // always, but a "Back to Transactions" link carries them all.
  const splitParam = (name) => searchParams.get(name)?.split(',').filter(Boolean) || [];
  const [methodFilter, setMethodFilter] = useState(splitParam('method'));
  const [statusFilter, setStatusFilter] = useState(splitParam('status'));
  const [logTypeFilter, setLogTypeFilter] = useState(splitParam('logType'));
  const [environmentFilter, setEnvironmentFilter] = useState(splitParam('environment'));

  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [timeWindow, setTimeWindow] = useState(searchParams.get('window') || '24h');
  const [sortField, setSortField] = useState(searchParams.get('sortField') || 'timestamp');
  const [sortDirection, setSortDirection] = useState(searchParams.get('sortDirection') || 'desc');

  const { colWidths, tableWidth, handleResizeStart } = useResizableColumns(COLUMNS);

  useEffect(() => {
    const fetchMeta = async () => {
      setComponentsLoading(true);
      try {
        const [compRes, tenantRes] = await Promise.allSettled([getComponents(), getTenants()]);
        if (compRes.status === 'fulfilled') {
          setComponents(Array.isArray(compRes.value.data) ? compRes.value.data : []);
        } else {
          setComponentsError('Failed to load components.');
        }
        if (tenantRes.status === 'fulfilled') {
          const d = tenantRes.value.data;
          setTenants(Array.isArray(d) ? d : []);
        }
      } finally {
        setComponentsLoading(false);
      }
    };
    fetchMeta();
  }, []);

  /**
   * @param restoreToPage when set (returning from a detail view via "Back to
   *   Transactions"), keep pulling cursor pages after the first until there is
   *   enough data to land on that page, or the backend runs out — otherwise
   *   pagination here is a client-side slice over whatever's been loaded, and
   *   restoring page 5 with only page 1 loaded would just show page 1.
   */
  const fetchTransactions = useCallback(async (name, tenant, restoreToPage) => {
    if (!name || !tenant) return;
    setLoading(true);
    setError('');
    setTransactions([]);
    setPageToken(null);
    setIsLastPage(false);
    setCurrentPage(1);
    try {
      let content = [];
      let nextToken = null;
      let last = true;

      const first = await getBusinessTransactionsPaged(name, tenant, null);
      content = first.data.content || [];
      nextToken = first.data.nextPageToken || null;
      last = first.data.last ?? true;

      while (restoreToPage && content.length < restoreToPage * PAGE_SIZE && !last) {
        const more = await getBusinessTransactionsPaged(name, tenant, nextToken);
        content = [...content, ...(more.data.content || [])];
        nextToken = more.data.nextPageToken || null;
        last = more.data.last ?? true;
      }

      setTransactions(content);
      setPageToken(nextToken);
      setIsLastPage(last);
      if (restoreToPage) {
        setCurrentPage(Math.max(1, Math.min(restoreToPage, Math.ceil(content.length / PAGE_SIZE) || 1)));
      }
    } catch {
      setError('Failed to load business transactions.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMoreTransactions = useCallback(async () => {
    if (isLastPage || !pageToken || loadingMore || !componentName || !tenantId) return;
    setLoadingMore(true);
    try {
      const response = await getBusinessTransactionsPaged(componentName, tenantId, pageToken);
      const data = response.data;
      setTransactions((prev) => [...prev, ...(data.content || [])]);
      setPageToken(data.nextPageToken || null);
      setIsLastPage(data.last ?? true);
    } catch {
      setError('Failed to load more transactions.');
    } finally {
      setLoadingMore(false);
    }
  }, [componentName, tenantId, isLastPage, pageToken, loadingMore]);

  // An explicit tenant/component pick always starts fresh at page 1, and any
  // restore params left over from a "Back to Transactions" link (page, plus
  // every filter/sort/search value below) would misapply to whatever's
  // picked next on a later reload — so they're dropped from the URL here,
  // not just reset in state.
  const RESTORE_PARAM_KEYS = ['page', 'method', 'status', 'logType', 'environment', 'q', 'window', 'sortField', 'sortDirection'];
  const withoutRestoreParams = (params, overrides) => {
    const next = Object.fromEntries(params);
    RESTORE_PARAM_KEYS.forEach((key) => delete next[key]);
    return { ...next, ...overrides };
  };

  const handleComponentChange = (name) => {
    setComponentName(name);
    setCurrentPage(1);
    setSearchParams((p) => withoutRestoreParams(p, { component: name }));
    if (name && tenantId) fetchTransactions(name, tenantId);
    else setTransactions([]);
  };

  const handleTenantChange = (val) => {
    setTenantId(val);
    setCurrentPage(1);
    setSearchParams((p) => withoutRestoreParams(p, { tenant: val }));
    if (componentName && val) fetchTransactions(componentName, val);
    else setTransactions([]);
  };

  // Auto-fetch if both were pre-selected via URL params. This is also how a
  // detail view coming back via "Back to Transactions" restores the page it
  // came from — that button returns to this same URL with &page=N appended
  // (see handleRowClick below), rather than routing state, because tenant and
  // component already live in this URL and a restored page is meaningless
  // without them: state can't put them back once this route has already
  // been reached without them in it.
  useEffect(() => {
    const preComp = searchParams.get('component');
    const preTenant = searchParams.get('tenant');
    const pageParam = Number(searchParams.get('page'));
    const prePage = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : undefined;
    if (preComp && preTenant) fetchTransactions(preComp, preTenant, prePage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const httpMethods = useMemo(
    () => [...new Set(transactions.map((tx) => tx.httpMethod).filter(Boolean))].sort(),
    [transactions]
  );

  const logTypes = useMemo(
    () => [...new Set(transactions.map((tx) => tx.logType).filter(Boolean))].sort(),
    [transactions]
  );

  const environments = useMemo(
    () => [...new Set(transactions.map((tx) => tx.environment).filter(Boolean))].sort(),
    [transactions]
  );

  const statusOptions = ['2xx', '4xx', '5xx'];

  const getStatusCategory = (code) => {
    if (!code) return 'unknown';
    if (code >= 200 && code < 300) return 'success';
    if (code >= 400 && code < 500) return 'client-error';
    if (code >= 500) return 'server-error';
    return 'other';
  };

  const getStatusBucket = (code) => {
    if (code >= 200 && code < 300) return '2xx';
    if (code >= 400 && code < 500) return '4xx';
    if (code >= 500) return '5xx';
    return null;
  };

  const hasActiveFilters =
    methodFilter.length > 0 ||
    statusFilter.length > 0 ||
    logTypeFilter.length > 0 ||
    environmentFilter.length > 0 ||
    searchQuery.trim() !== '';

  const clearAllFilters = useCallback(() => {
    setMethodFilter([]);
    setStatusFilter([]);
    setLogTypeFilter([]);
    setEnvironmentFilter([]);
    setSearchQuery('');
    setCurrentPage(1);
  }, []);

  const timeWindowMs = { '1h': 60 * 60 * 1000, '2h': 2 * 60 * 60 * 1000, '24h': 24 * 60 * 60 * 1000 };

  const filteredTransactions = useMemo(() => transactions
    .filter((tx) => methodFilter.length === 0 || methodFilter.includes(tx.httpMethod))
    .filter((tx) => logTypeFilter.length === 0 || logTypeFilter.includes(tx.logType))
    .filter((tx) => environmentFilter.length === 0 || environmentFilter.includes(tx.environment))
    .filter((tx) => {
      if (statusFilter.length === 0) return true;
      return statusFilter.includes(getStatusBucket(tx.statusCode));
    })
    .filter((tx) => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (
        tx.endpoint?.toLowerCase().includes(q) ||
        tx.id?.toLowerCase().includes(q) ||
        tx.transactionId?.toLowerCase().includes(q) ||
        tx.correlationId?.toLowerCase().includes(q) ||
        tx.userId?.toLowerCase().includes(q) ||
        tx.tenantId?.toLowerCase().includes(q) ||
        tx.bucketName?.toLowerCase().includes(q)
      );
    })
    .filter((tx) => {
      const cutoff = Date.now() - (timeWindowMs[timeWindow] || timeWindowMs['24h']);
      const txTime = tx.timestamp ? new Date(tx.timestamp).getTime() : 0;
      return txTime >= cutoff;
    })
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === 'timestamp') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      } else if (sortField === 'durationMs' || sortField === 'statusCode') {
        aVal = aVal || 0;
        bVal = bVal || 0;
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    }), [transactions, methodFilter, logTypeFilter, environmentFilter, statusFilter, searchQuery, timeWindow, timeWindowMs, sortField, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(filteredTransactions.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pageEnd = pageStart + PAGE_SIZE;
  const pagedTransactions = filteredTransactions.slice(pageStart, pageEnd);

  // When navigating to the last loaded page and more data exists, fetch it
  const handleNextPage = useCallback(async () => {
    const nextPage = safePage + 1;
    // If next page would exceed what we have loaded, fetch more first
    if (nextPage * PAGE_SIZE > transactions.length && !isLastPage) {
      await loadMoreTransactions();
    }
    setCurrentPage(nextPage);
  }, [safePage, transactions.length, isLastPage, loadMoreTransactions]);

  const handlePrevPage = () => {
    setCurrentPage((p) => Math.max(1, p - 1));
  };

  // Reset to page 1 when filters change. useUpdateEffect (not useEffect) so
  // this doesn't fire on mount — that would immediately overwrite the page
  // restored by fetchTransactions above.
  useUpdateEffect(() => {
    setCurrentPage(1);
  }, [methodFilter, statusFilter, logTypeFilter, environmentFilter, searchQuery, timeWindow]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const getSortIndicator = (field) => {
    const icon = sortField === field ? (sortDirection === 'asc' ? '▲' : '▼') : '';
    return <span className="bt-sort-icon">{icon}</span>;
  };

  const formatDuration = (ms) => {
    if (ms == null) return '—';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleString();
  };

  const handleRowClick = (tx) => {
    const params = new URLSearchParams({ tenant: tenantId, component: componentName, page: String(safePage) });
    if (methodFilter.length) params.set('method', methodFilter.join(','));
    if (statusFilter.length) params.set('status', statusFilter.join(','));
    if (logTypeFilter.length) params.set('logType', logTypeFilter.join(','));
    if (environmentFilter.length) params.set('environment', environmentFilter.join(','));
    if (searchQuery.trim()) params.set('q', searchQuery);
    if (timeWindow !== '24h') params.set('window', timeWindow);
    if (sortField !== 'timestamp') params.set('sortField', sortField);
    if (sortDirection !== 'desc') params.set('sortDirection', sortDirection);

    navigate(`/business-transactions/${tx.id}`, {
      state: { returnTo: `/business-transactions?${params.toString()}` },
    });
  };

  const canGoNext = safePage < totalPages || (!isLastPage && !loadingMore);
  const canGoPrev = safePage > 1;

  return (
    <div className="page-container">
      {loading && <LoadingSpinner message="Loading business transactions..." />}

      <div className="bt-header">
        <h1 className="page-title">Business Transactions</h1>
        <div className="bt-stats">
          <div className="bt-stat">
            <span className="bt-stat-value">{transactions.length}</span>
            <span className="bt-stat-label">Loaded</span>
          </div>
          <div className="bt-stat bt-stat--success">
            <span className="bt-stat-value">
              {transactions.filter((tx) => tx.statusCode >= 200 && tx.statusCode < 300).length}
            </span>
            <span className="bt-stat-label">Success</span>
          </div>
          <div className="bt-stat bt-stat--error">
            <span className="bt-stat-value">
              {transactions.filter((tx) => tx.statusCode >= 400).length}
            </span>
            <span className="bt-stat-label">Errors</span>
          </div>
        </div>
      </div>

      {componentsError && <div className="alert alert-error">{componentsError}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {/* Tenant + Component selectors */}
      <div className="bt-selectors">
        <div className="bt-selector-group">
          <span className="bt-component-label">Tenant</span>
          <BtDropdown
            value={tenantId}
            placeholder="Select tenant…"
            options={tenants}
            onChange={handleTenantChange}
          />
        </div>
        <div className="bt-selector-sep" />
        <div className="bt-selector-group">
          <span className="bt-component-label">Component</span>
          {componentsLoading ? (
            <span className="bt-component-loading">Loading...</span>
          ) : (
            <BtDropdown
              value={componentName}
              placeholder="Select component…"
              options={components}
              onChange={handleComponentChange}
            />
          )}
        </div>
      </div>

      {/* Filter bar */}
      <div className="bt-filter-bar">
        <div className="bt-filter-row">
          <div className="bt-search-field">
            <svg className="bt-search-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            <input
              type="text"
              className="bt-search-input"
              placeholder="Search endpoint, ID, user…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="bt-search-clear" onClick={() => setSearchQuery('')}>✕</button>
            )}
          </div>

          <FilterDropdown label="Status" options={statusOptions} selected={statusFilter} onChange={setStatusFilter} />
          <FilterDropdown label="Method" options={httpMethods} selected={methodFilter} onChange={setMethodFilter} />
          {logTypes.length > 0 && (
            <FilterDropdown label="Log Type" options={logTypes} selected={logTypeFilter} onChange={setLogTypeFilter} />
          )}
          {environments.length > 0 && (
            <FilterDropdown label="Environment" options={environments} selected={environmentFilter} onChange={setEnvironmentFilter} />
          )}
          {hasActiveFilters && (
            <button className="bt-clear-all" onClick={clearAllFilters}>Clear filters</button>
          )}
        </div>

        <div className="bt-filter-row">
          <div className="bt-time-window">
            {['1h', '2h', '24h'].map((w) => (
              <button
                key={w}
                className={`bt-time-btn${timeWindow === w ? ' bt-time-btn--active' : ''}`}
                onClick={() => setTimeWindow(w)}
                type="button"
              >
                {w}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Prompt to select both */}
      {(!componentName || !tenantId) && (
        <div className="card">
          <p className="empty-state">
            {!tenantId ? 'Select a tenant and component' : 'Select a component'} to view transactions.
          </p>
        </div>
      )}

      {/* Transactions table */}
      {componentName && tenantId && (
        <div className="card">
          <div className="bt-table-toolbar">
            <span className="bt-table-count">
              {filteredTransactions.length > 0
                ? `${pageStart + 1}–${Math.min(pageEnd, filteredTransactions.length)} of ${filteredTransactions.length} transactions`
                : '0 transactions'}
              {!isLastPage && <span className="bt-load-hint"> · more available</span>}
            </span>

            <div className="bt-toolbar-right">
              <button className="btn btn-secondary bt-refresh-btn" onClick={() => fetchTransactions(componentName, tenantId)}>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
                Refresh
              </button>

              <div className="bt-pagination">
                <button
                  className="bt-page-btn"
                  onClick={handlePrevPage}
                  disabled={!canGoPrev}
                  title="Previous page"
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                <span className="bt-page-indicator">
                  {loadingMore ? (
                    <span className="bt-page-loading">Loading…</span>
                  ) : (
                    <>{safePage} / {totalPages}</>
                  )}
                </span>
                <button
                  className="bt-page-btn"
                  onClick={handleNextPage}
                  disabled={!canGoNext || loadingMore}
                  title="Next page"
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {filteredTransactions.length === 0 ? (
            <p className="empty-state">No transactions found.</p>
          ) : (
            <div className="bt-table-wrapper">
              <table
                className="bt-table resizable-table"
                style={{ width: tableWidth }}
              >
                <colgroup>
                  {COLUMNS.map((c) => (
                    <col key={c.key} style={{ width: `${colWidths[c.key]}px` }} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    {COLUMNS.map((c) => (
                      <th
                        key={c.key}
                        className={`resizable-th${c.key === 'arrow' ? '' : ' bt-th-sortable'}`}
                        onClick={c.key === 'arrow' ? undefined : () => handleSort(c.key)}
                      >
                        {c.label}
                        {c.key !== 'arrow' && getSortIndicator(c.key)}
                        <span
                          className="col-resize-handle"
                          onMouseDown={handleResizeStart(c.key)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagedTransactions.map((tx) => (
                    <tr
                      key={tx.id || `${tx.componentName}-${tx.transactionId}`}
                      className={`bt-table-row${tx.statusCode >= 400 ? ' bt-table-row--error' : ''}`}
                      onClick={() => handleRowClick(tx)}
                    >
                      <td className="bt-txn-id" title={tx.id}>{tx.id || '—'}</td>
                      <td className="bt-txn-id" title={tx.transactionId}>{tx.transactionId || '—'}</td>
                      <td className="bt-timestamp">{formatTimestamp(tx.timestamp)}</td>
                      <td><span className="bt-component-badge" title={tx.componentName}>{tx.componentName || '—'}</span></td>
                      <td><span className={`bt-env-badge bt-env--${(tx.environment || '').toLowerCase()}`}>{tx.environment || '—'}</span></td>
                      <td><span className={`bt-method bt-method--${(tx.httpMethod || '').toLowerCase()}`}>{tx.httpMethod || '—'}</span></td>
                      <td className="bt-endpoint" title={tx.endpoint}>{tx.endpoint || '—'}</td>
                      <td><span className={`bt-status-code bt-status-code--${getStatusCategory(tx.statusCode)}`}>{tx.statusCode || '—'}</span></td>
                      <td className="bt-duration">{formatDuration(tx.durationMs)}</td>
                      <td className="bt-user" title={tx.userId}>{tx.userId || '—'}</td>
                      <td className="bt-tenant" title={tx.tenantId}>{tx.tenantId || '—'}</td>
                      <td>
                        <span className="bt-row-arrow">→</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default BusinessTransactionsPage;
